var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Transition from 'react-transition-group/Transition';
import { reset, SubmissionError } from 'redux-form';
import { apiGetCategorySelect } from '../../actions/CategoryActions';
import { apiCreateHelpContact } from '../../actions/ContactActions';
import { HELP_CONTACT_CATEGORY_USER_TYPE_CLIENT, HELP_CONTACT_CATEGORY_USER_TYPE_OTHER, HELP_CONTACT_CATEGORY_USER_TYPE_PROFESSIONAL, HELP_CONTACT_CATEGORY_USER_TYPE_PROFFIT_SEARCH, } from '../../define/consts/contact';
import { wrapDispatch } from '../../helpers/action';
import { isClient, isProfessional } from '../../helpers/auth';
import { sleep, reDraw } from '../../helpers/common';
import { createValidateErrorData } from '../../helpers/validate';
import GoogleAnalytics from '../../vendor/GoogleAnalytics';
import ModalPortal from '../Ui/Modal/ModalPortal';
import HelpContent from './HelpContent';
import HelpFixedButton from './HelpFixedButton';
const formName = 'HelpForm';
const Help = () => {
    const unMountRef = useRef(false);
    const mainRef = useRef(null);
    const profile = useSelector((state) => state.profile || {});
    const auth = useSelector((state) => state.auth);
    const rawHelpContactCategories = useSelector((state) => state.category.helpContactCategories || []);
    const location = useLocation();
    // /proffit_search下のページか判定
    const isProffitSearchPage = useMemo(() => {
        const path = location.pathname.replace(/\/$/, '');
        return path.indexOf('/proffit_search') === 0;
    }, [location.pathname]);
    const helpContactCategories = useMemo(() => {
        const filters = [HELP_CONTACT_CATEGORY_USER_TYPE_OTHER];
        if (isProffitSearchPage) {
            filters.push(HELP_CONTACT_CATEGORY_USER_TYPE_PROFFIT_SEARCH);
        }
        else {
            if (isClient(auth)) {
                filters.push(HELP_CONTACT_CATEGORY_USER_TYPE_CLIENT);
            }
            if (isProfessional(auth)) {
                filters.push(HELP_CONTACT_CATEGORY_USER_TYPE_PROFESSIONAL);
            }
        }
        return rawHelpContactCategories.filter(({ userType }) => filters.includes(userType));
    }, [auth, rawHelpContactCategories, isProffitSearchPage]);
    const dispatch = useDispatch();
    const [isOpening, changeIsOpening] = useState(false);
    const [isOpen, rawChangeIsOpen] = useState(false);
    const [isSucceeded, changeIsSucceeded] = useState(false);
    const [contactType, changeContactType] = useState('mail');
    const changeIsOpen = useCallback((v) => {
        if (v) {
            const main = document.querySelector('#app > main');
            mainRef.current = main || null;
        }
        rawChangeIsOpen(v);
    }, [rawChangeIsOpen]);
    useEffect(() => () => {
        unMountRef.current = false;
    }, []);
    useEffect(() => {
        dispatch(apiGetCategorySelect('help_contact_category'));
    }, [dispatch]);
    const initialValues = useMemo(() => ({
        email: profile.email,
    }), [profile.email]);
    const onReset = useCallback(() => {
        changeIsOpening(false);
        changeIsOpen(false);
        changeIsSucceeded(false);
        changeContactType('mail');
    }, [changeIsOpening, changeIsOpen, changeIsSucceeded, changeContactType]);
    const onChangeTab = useCallback((e) => {
        changeContactType(e.currentTarget.value);
    }, [changeContactType]);
    const onOpen = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        GoogleAnalytics.sendClickEvent('help', 'help_btn_click', '');
        changeIsOpen(false);
        changeIsOpening(true);
        yield sleep(1);
        if (!unMountRef.current) {
            changeIsOpen(true);
            changeIsOpening(false);
        }
    }), [changeIsOpen, changeIsOpening, unMountRef]);
    const onSubmit = useCallback((values) => __awaiter(void 0, void 0, void 0, function* () {
        const { helpContactCategoryId: id, helpContactCategoryIds: ids } = values, params = __rest(values, ["helpContactCategoryId", "helpContactCategoryIds"]);
        try {
            yield wrapDispatch(dispatch, apiCreateHelpContact(Object.assign(Object.assign({}, params), { helpContactCategoryIds: id
                    ? [parseInt(id, 10)]
                    : ids.filter((v) => !!v), pageUrl: window.location.href })));
            if (!unMountRef.current) {
                dispatch(reset(formName));
                changeIsSucceeded(true);
                GoogleAnalytics.sendClickEvent('help', 'help_email_send', '');
            }
        }
        catch (e) {
            throw new SubmissionError(createValidateErrorData(e));
        }
    }), [dispatch, changeIsSucceeded, unMountRef]);
    const onClose = useCallback(() => {
        if (isSucceeded) {
            onReset();
        }
        else {
            changeIsOpen(false);
            changeContactType('mail');
        }
    }, [onReset, changeIsOpen, changeContactType, isSucceeded]);
    const onCancel = useCallback(() => {
        if (isSucceeded) {
            onReset();
        }
        else {
            changeIsOpen(false);
            changeContactType('mail');
            dispatch(reset(formName));
        }
    }, [dispatch, onReset, isSucceeded, changeContactType, changeIsOpen]);
    const onClickCall = useCallback(() => {
        GoogleAnalytics.sendClickEvent('help', 'help_webmtg_send', '');
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx(Transition, Object.assign({ in: !isOpen, mountOnEnter: true, onEnter: reDraw, timeout: 300, unmountOnExit: true }, { children: (state) => (_jsx("div", Object.assign({ className: 'help' }, { children: _jsx(HelpFixedButton, { className: state, isLoading: isOpening, onClick: onOpen }) }))) })), _jsx(ModalPortal, Object.assign({ selector: '#app' }, { children: _jsx(Transition, Object.assign({ in: isOpen, mountOnEnter: true, onEnter: reDraw, timeout: 300, unmountOnExit: true }, { children: (state) => (_jsx("div", Object.assign({ className: 'help' }, { children: _jsx(HelpContent, { className: state, contactType: contactType, helpContactCategories: helpContactCategories, initialValues: initialValues, isProffitSearchPage: isProffitSearchPage, isSucceeded: isSucceeded, onCancel: onCancel, onChangeTab: onChangeTab, onClickCall: onClickCall, onClose: onClose, onComplete: onReset, onSubmit: onSubmit }) }))) })) }))] }));
};
export default Help;
